import React, { useState, useRef, useEffect } from "react";

import { useSpring, config as springConfig, animated } from "@react-spring/web";

import { Box, ClickAwayListener } from "@mui/material";

import GettingStarted from "@app/analytics/GettingStarted";
import useUiStore from "@app/analytics/stores/useUiStore";
import useAuth from "@app/common/auth/useAuth";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import useWindowDimensions from "@app/common/hooks/useWindowDimensions";

import Stack from "@rui/Stack";

import SchedulingLink from "./SchedulingLink";
import SideNavAddRead from "./SideNavAddRead";
import SideNavContent from "./SideNavContent";
import SideNavFooter from "./SideNavFooter";
import { SideNavDivider } from "./styles";

interface SideNavProps {
  topOffset?: number;
}

const AnimatedBox = animated(Box);

const OPEN_WIDTH = "244px";
const CLOSED_WIDTH = "68px";
const FORCE_CLOSE_BREAKPOINT = 1300;

const SideNav = ({ topOffset }: SideNavProps) => {
  const { user } = useAuth();
  const { windowWidth } = useWindowDimensions();

  const sideNavModalVisible = useUiStore(
    (state) => state.promotionRedeemedModalId != null
  );

  const [initExpanded, setInitExpanded] = useLocalStorage(
    "isSideNavExpanded",
    true
  );
  const [expanded, setExpanded] = useState(initExpanded || sideNavModalVisible);
  const [open, setOpen] = useState(expanded);
  const [dirty, setDirty] = useState(false);
  const openTimeoutRef = useRef<number>();

  useEffect(() => {
    if (!sideNavModalVisible) return;
    // force the sidenav open when a modal is trying to highlight the content
    setOpen(true);
  }, [sideNavModalVisible]);

  useEffect(() => {
    if (!initExpanded || sideNavModalVisible) return;
    if (windowWidth > FORCE_CLOSE_BREAKPOINT) {
      // re-open the sidenav when the window is wide
      setOpen(true);
      setExpanded(true);
    } else if (windowWidth < FORCE_CLOSE_BREAKPOINT) {
      // force the sidenav to collapse when under a certain width to give more
      // space for content
      setOpen(false);
      setExpanded(false);
    }
  }, [windowWidth, sideNavModalVisible]);

  const springNavWidth = useSpring({
    from: {
      width:
        (open && expanded) || (!open && !expanded && dirty)
          ? OPEN_WIDTH
          : CLOSED_WIDTH,
    },
    to: { width: open ? OPEN_WIDTH : CLOSED_WIDTH },
    config: { ...springConfig.stiff, tension: 400, mass: 0.5 },
  });

  const springExpandedWidth = useSpring({
    from: { width: expanded !== dirty ? OPEN_WIDTH : CLOSED_WIDTH },
    to: { width: expanded ? OPEN_WIDTH : CLOSED_WIDTH },
    config: { ...springConfig.stiff, tension: 400, mass: 0.5 },
  });

  const handleMouseEnter = () => {
    clearTimeout(openTimeoutRef.current);
    openTimeoutRef.current = window.setTimeout(
      () => setOpen(expanded || true),
      250
    );
  };

  const handleMouseLeave = () => {
    clearTimeout(openTimeoutRef.current);
    setOpen(expanded || false);
  };

  const handleToggleExpanded = (expand: boolean) => {
    if (!expand) {
      setOpen(false);
    }
    setDirty(true);
    setExpanded(expand);
    setInitExpanded(expand);
  };

  const handleMenuClose = () => {
    if (!expanded) {
      setOpen(false);
    }
  };

  const handleClickAway = () => {
    if (sideNavModalVisible) return;
    setOpen(expanded || false);
  };

  return (
    // Pushes the page content by the same width as the side nav
    <AnimatedBox
      style={springExpandedWidth}
      sx={{ position: "relative", flexShrink: 0 }}
    >
      <GettingStarted sideNavOpen={open} />
      <ClickAwayListener onClickAway={handleClickAway}>
        {/* fixes the nav bar to the page */}
        <AnimatedBox
          style={springNavWidth}
          sx={{
            height: topOffset ? `calc(100vh - ${topOffset}px)` : "100vh",
            position: "fixed",
            top: topOffset ? `${topOffset}px` : "0",
            zIndex: 100,
            flexShrink: 0,
            overflow: "hidden",
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* lots of wrappers to get the footer to display at the
         bottom of the fixed container and the nav elements to scroll */}
          <Box sx={{ height: "100%", overflow: "hidden auto" }}>
            <Stack
              sx={{
                height: "100%",
                padding: "10px 16px",
                bgcolor: "overlay.primary.12",
                position: "relative",
              }}
              alignItems="flex-start"
            >
              <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
                <Stack
                  sx={{ position: "absolute", width: "100%", height: "100%" }}
                >
                  <SideNavContent
                    expanded={expanded}
                    onToggleExpanded={() => handleToggleExpanded(!expanded)}
                    open={open}
                    sideNavModalVisible={sideNavModalVisible}
                    onClose={handleMenuClose}
                  />
                </Stack>
              </Box>
              <Stack sx={{ width: "100%" }} spacing={1}>
                <SideNavAddRead expanded={open} />
                <SchedulingLink expanded={open} />
                <SideNavDivider sx={{ mb: 0 }} />
                <SideNavFooter
                  user={user}
                  showAvatar={!open}
                  onClose={handleMenuClose}
                />
              </Stack>
            </Stack>
          </Box>
        </AnimatedBox>
      </ClickAwayListener>
    </AnimatedBox>
  );
};

export default SideNav;
